import React, { useEffect, useMemo, useState } from 'react';
import {
  updateLscLiveAutomationPolicy,
  LscApiError,
  type UpdateLscLiveAutomationPolicyPayload,
} from '../api';
import {
  isScheduleCorrectedAutoShowEnabled,
  SCHEDULE_CORRECTED_LIVE_BEHAVIOR_OPTIONS,
  UNKNOWN_MATCHUP_BEHAVIOR_OPTIONS,
} from '../liveAutomationPolicy';
import type { LscLeague, LscLiveAutomationPolicy } from '../types';
import ErrorBanner from './ErrorBanner';

const FIELD_CLASS =
  'w-full rounded-lg border border-secondary-300 bg-white px-3 py-2 text-secondary-900 ' +
  'placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent';

interface LiveAutomationPanelProps {
  league: LscLeague;
  leagueArchived?: boolean;
  onUpdated?: (league: LscLeague) => void;
}

function policyFromLeague(league: LscLeague): LscLiveAutomationPolicy {
  return (
    league.live_automation_policy ?? {
      auto_show_verified_live_games: false,
      minimum_live_confidence: 90,
      schedule_corrected_live_behavior: 'require_review',
      minimum_schedule_corrected_live_confidence: 95,
      unknown_matchup_behavior: 'needs_attention',
      auto_create_live_needs_attention_exceptions: true,
    }
  );
}

const LiveAutomationPanel: React.FC<LiveAutomationPanelProps> = ({
  league,
  leagueArchived = false,
  onUpdated,
}) => {
  const initial = useMemo(() => policyFromLeague(league), [league]);
  const [draft, setDraft] = useState<LscLiveAutomationPolicy>(initial);
  const [changeReason, setChangeReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    setDraft(policyFromLeague(league));
  }, [league]);

  const correctedAutoShowEnabled = isScheduleCorrectedAutoShowEnabled(
    draft.schedule_corrected_live_behavior
  );

  const updateDraft = (patch: Partial<LscLiveAutomationPolicy>) => {
    setDraft((current) => ({ ...current, ...patch }));
  };

  const handleSave = async () => {
    if (!league.id) return;
    const minLive = Number(draft.minimum_live_confidence ?? 90);
    const minCorrected = Number(draft.minimum_schedule_corrected_live_confidence ?? 95);
    if (minLive < 75 || minLive > 100 || minCorrected < 75 || minCorrected > 100) {
      setError('Confidence scores must be between 75 and 100.');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);
    const payload: UpdateLscLiveAutomationPolicyPayload = {
      live_automation_policy: {
        auto_show_verified_live_games: draft.auto_show_verified_live_games,
        minimum_live_confidence: minLive,
        schedule_corrected_live_behavior: draft.schedule_corrected_live_behavior,
        minimum_schedule_corrected_live_confidence: minCorrected,
        unknown_matchup_behavior: draft.unknown_matchup_behavior,
        auto_create_live_needs_attention_exceptions:
          draft.auto_create_live_needs_attention_exceptions,
      },
      change_reason: changeReason.trim() || undefined,
    };

    try {
      const updated = await updateLscLiveAutomationPolicy(league.id, payload);
      setSuccess('Live automation policy updated.');
      onUpdated?.(updated);
    } catch (err) {
      setError(err instanceof LscApiError ? err.message : 'Could not save live automation policy.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="live-automation-panel">
      <div
        className="card border border-amber-700/40 bg-amber-950/20 text-sm text-secondary-200 space-y-2"
        data-testid="live-automation-warning"
      >
        <p className="font-medium text-amber-200">
          Live Automation does not publish permanent archive replays.
        </p>
        <p className="text-secondary-400">
          Archive Automation controls published VODs after a stream ends.
        </p>
      </div>

      {error ? <ErrorBanner message={error} /> : null}
      {success ? (
        <div className="text-sm text-emerald-300" data-testid="live-automation-status">
          {success}
        </div>
      ) : null}

      <div className="card space-y-5 text-sm">
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            className="mt-1"
            checked={Boolean(draft.auto_show_verified_live_games)}
            disabled={leagueArchived}
            onChange={(event) =>
              updateDraft({ auto_show_verified_live_games: event.target.checked })
            }
            data-testid="live-automation-auto-show"
          />
          <span>
            <span className="font-medium text-white block">Auto-show verified live games</span>
            <span className="text-secondary-400">
              When enabled, verified league games that meet your confidence threshold can appear on
              League Home while the stream is live.
            </span>
          </span>
        </label>

        <div className="space-y-2">
          <label className="font-medium text-white" htmlFor="minimum-live-confidence">
            Minimum live confidence
          </label>
          <p className="text-secondary-400">
            Only streams at or above this confidence score (75–100) are eligible for auto-show.
          </p>
          <input
            id="minimum-live-confidence"
            type="number"
            min={75}
            max={100}
            className={FIELD_CLASS}
            value={draft.minimum_live_confidence ?? 90}
            disabled={leagueArchived}
            onChange={(event) =>
              updateDraft({ minimum_live_confidence: Number(event.target.value) })
            }
            data-testid="live-automation-min-confidence"
          />
        </div>

        <div className="space-y-2">
          <label className="font-medium text-white" htmlFor="schedule-corrected-behavior">
            Schedule-corrected games
          </label>
          <select
            id="schedule-corrected-behavior"
            className={FIELD_CLASS}
            value={draft.schedule_corrected_live_behavior ?? 'require_review'}
            disabled={leagueArchived}
            onChange={(event) =>
              updateDraft({ schedule_corrected_live_behavior: event.target.value })
            }
            data-testid="live-automation-schedule-corrected-behavior"
          >
            {SCHEDULE_CORRECTED_LIVE_BEHAVIOR_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <p className="text-secondary-400">
            {
              SCHEDULE_CORRECTED_LIVE_BEHAVIOR_OPTIONS.find(
                (option) => option.value === draft.schedule_corrected_live_behavior
              )?.description
            }
          </p>
        </div>

        <div className="space-y-2">
          <label
            className="font-medium text-white"
            htmlFor="minimum-schedule-corrected-confidence"
          >
            Minimum confidence for corrected games
          </label>
          <input
            id="minimum-schedule-corrected-confidence"
            type="number"
            min={75}
            max={100}
            className={FIELD_CLASS}
            value={draft.minimum_schedule_corrected_live_confidence ?? 95}
            disabled={leagueArchived || !correctedAutoShowEnabled}
            onChange={(event) =>
              updateDraft({
                minimum_schedule_corrected_live_confidence: Number(event.target.value),
              })
            }
            data-testid="live-automation-min-corrected-confidence"
          />
        </div>

        <div className="space-y-2">
          <label className="font-medium text-white" htmlFor="unknown-matchup-behavior">
            Unknown matchup behavior
          </label>
          <select
            id="unknown-matchup-behavior"
            className={FIELD_CLASS}
            value={draft.unknown_matchup_behavior ?? 'needs_attention'}
            disabled={leagueArchived}
            onChange={(event) =>
              updateDraft({ unknown_matchup_behavior: event.target.value })
            }
            data-testid="live-automation-unknown-matchup-behavior"
          >
            {UNKNOWN_MATCHUP_BEHAVIOR_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            className="mt-1"
            checked={Boolean(draft.auto_create_live_needs_attention_exceptions)}
            disabled={leagueArchived}
            onChange={(event) =>
              updateDraft({
                auto_create_live_needs_attention_exceptions: event.target.checked,
              })
            }
            data-testid="live-automation-auto-create-na"
          />
          <span>
            <span className="font-medium text-white block">
              Send uncertain games to Needs Attention
            </span>
            <span className="text-secondary-400">
              Automatically create Needs Attention items for live streams that cannot be verified.
            </span>
          </span>
        </label>

        <div className="space-y-2">
          <label className="font-medium text-white" htmlFor="live-automation-change-reason">
            Change reason (optional)
          </label>
          <textarea
            id="live-automation-change-reason"
            className={FIELD_CLASS}
            rows={2}
            value={changeReason}
            disabled={leagueArchived}
            onChange={(event) => setChangeReason(event.target.value)}
          />
        </div>

        <button
          type="button"
          className="btn-primary"
          disabled={saving || leagueArchived}
          onClick={() => void handleSave()}
          data-testid="live-automation-save"
        >
          {saving ? 'Saving…' : 'Save live automation settings'}
        </button>
      </div>
    </div>
  );
};

export default LiveAutomationPanel;
